import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  CircularProgress,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from '@mui/material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { applyPatch, Operation } from 'fast-json-patch';

import ContextReferenceSelector from '../../common/ContextReferenceSelector';
import { Slide, SuggestedOutlineEditsResult, JsonPatchOperation } from '../../../types';

interface EditOutlineWizardDialogProps {
  /** Dialog open state */
  open: boolean;
  /** Fired when the dialog (and wizard) should be closed */
  onClose: () => void;
  /** UI language */
  lang: string;
  /** The project id (for ContextReferenceSelector) */
  projectId: string;
  /** Outline items to be edited (slides array) */
  outlineItems: Slide[];
  /** Callback invoked after edits are applied */
  onApplyOutline: (updatedSlides: Slide[]) => void;
  /** Function that asks backend for JSON-patch edits */
  handleSuggestOutlineEdits: (instruction: string, fileIds?: string[]) => Promise<SuggestedOutlineEditsResult>;
}

function EditOutlineWizardDialog({
  open,
  onClose,
  lang,
  projectId,
  outlineItems,
  onApplyOutline,
  handleSuggestOutlineEdits,
}: EditOutlineWizardDialogProps) {
  const [wizardStep, setWizardStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // step 0 inputs
  const [editInstruction, setEditInstruction] = useState('');
  const [editFileIds, setEditFileIds] = useState<string[] | undefined>();

  // step 1 result
  const [outlineEditsComment, setOutlineEditsComment] = useState('');
  const [outlineEditsAffectedSlides, setOutlineEditsAffectedSlides] = useState<number[]>([]);
  const [suggestedOutlineEdits, setSuggestedOutlineEdits] = useState<JsonPatchOperation[]>([]);

  const resetState = () => {
    setWizardStep(0);
    setEditInstruction('');
    setEditFileIds(undefined);
    setOutlineEditsComment('');
    setOutlineEditsAffectedSlides([]);
    setSuggestedOutlineEdits([]);
    setLoading(false);
  };

  const handleDialogClose = () => {
    resetState();
    onClose();
  };

  const generateOutlineEdits = async () => {
    setLoading(true);
    try {
      const result = await handleSuggestOutlineEdits(editInstruction, editFileIds);
      if (result) {
        setOutlineEditsComment(result.comment);
        setOutlineEditsAffectedSlides(result.affectedSlides);
        setSuggestedOutlineEdits(result.edits);
      }
      setWizardStep(1);
    } catch (error) {
      console.error('Error suggesting outline edits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOutlineEdits = () => {
    try {
      const slidesCopy: Slide[] = JSON.parse(JSON.stringify(outlineItems));
      // fast-json-patch expects its own Operation type; cast conversion
      const result = applyPatch(slidesCopy, suggestedOutlineEdits as unknown as Operation[], false, true);
      onApplyOutline(result.newDocument);
    } catch (error) {
      console.error('Failed to apply outline edits:', error);
    } finally {
      handleDialogClose();

      if (outlineEditsAffectedSlides && outlineEditsAffectedSlides.length > 0) {
        const firstIndex = outlineEditsAffectedSlides[0];
        setTimeout(() => {
          const slideElements = document.querySelectorAll('.slides-editor > .MuiPaper-root');
          const targetSlide = slideElements[firstIndex] as HTMLElement | undefined;
          targetSlide?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 600);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-outline-wizard-title"
    >
      <DialogTitle id="edit-outline-wizard-title">
        {lang === 'en' ? 'Edit Outline' : '编辑大纲'}
      </DialogTitle>
      <DialogContent>
        {wizardStep === 0 && (
          <Stack spacing={2} mt={2}>
            <Box>
              <ContextReferenceSelector
                lang={lang}
                projectId={projectId}
                onFileIdsChange={setEditFileIds}
              />
            </Box>
            <TextField
              variant="filled"
              fullWidth
              multiline
              rows={4}
              label={lang === 'en' ? 'Enter your instruction for editing the outline' : '输入编辑大纲的指令'}
              placeholder={lang === 'en' ? 'e.g., Add more slides about the product features' : '例如：添加更多关于产品特性的幻灯片'}
              value={editInstruction}
              onChange={(e) => setEditInstruction(e.target.value)}
            />
          </Stack>
        )}

        {wizardStep === 1 && (
          <Box mt={2}>
            {loading ? (
              <Stack direction="row" justifyContent="center" my={4}>
                <CircularProgress />
              </Stack>
            ) : (
              <>
                <Typography gutterBottom>{outlineEditsComment}</Typography>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography>{lang === 'en' ? 'Show Patches Code' : '查看修订代码'}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '12px' }}>
                      {JSON.stringify(suggestedOutlineEdits, null, 2)}
                    </pre>
                  </AccordionDetails>
                </Accordion>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
        {wizardStep === 0 && (
          <Button 
            onClick={generateOutlineEdits} 
            variant="contained" 
            disabled={loading || !editInstruction.trim()}
            endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ChevronRight />}
          >
            {lang === 'en' ? 'Next' : '下一步'}
          </Button>
        )}
        {wizardStep === 1 && (
          <Button onClick={handleConfirmOutlineEdits} color="primary" variant="contained" disabled={loading}>
            {lang === 'en' ? 'Apply Edits' : '执行修改'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default EditOutlineWizardDialog; 